/**
 * Installing the app, without waiting for the browser to suggest it.
 *
 * Everything Chrome needs in order to *allow* an install is already in place —
 * HTTPS, a linked manifest, real 192 and 512 icons, `display: standalone`, a
 * service worker with a `fetch` handler. What is not in our hands is whether
 * Chrome ever *offers* it:
 *
 *   - on Android the mini-infobar appears at most once, and dismissing it
 *     suppresses the offer for around ninety days on that origin;
 *   - on desktop the offer is a small icon at the end of the omnibox, which is
 *     easy to miss and easy to never look at again;
 *   - `beforeinstallprompt` fires only once Chrome's engagement heuristic is
 *     satisfied, which is not something a page can ask for;
 *   - Safari on iOS never fires it at all. *Partager → Sur l'écran d'accueil*
 *     is the only path, and no script can open it.
 *
 * So the browser's own prompt is treated as a bonus rather than the mechanism.
 * This module keeps the deferred event alive and hands the app a button that
 * works whenever Chrome is willing, and an explanation when it is not.
 *
 * **The listener has to be registered before the app renders.** Chrome fires
 * `beforeinstallprompt` early — often before Preact has mounted anything — and
 * an event nobody called `preventDefault()` on is gone for the session. That is
 * why `watchInstall()` is called from `src/main.tsx` beside `registerSW`, and
 * why the event is held here rather than in component state.
 */

/** Not in `lib.dom`: it is Chromium-only and unlikely ever to be standardised. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/** Versioned like every other key here, so it can be abandoned rather than migrated. */
const DISMISSED_KEY = 'maison_zen_install_dismissed_v1'

let deferred: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()

function notify() {
  for (const listener of listeners) listener()
}

/**
 * Start holding on to Chrome's offer. Call once, before the first render.
 *
 * `preventDefault()` is what stops the mini-infobar appearing on its own — the
 * trade being that from here on the app owes the user a button, because Chrome
 * will not show one.
 */
export function watchInstall(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    notify()
  })

  window.addEventListener('appinstalled', () => {
    deferred = null
    // Remembered as well as cleared: `isStandalone()` is false in the tab the
    // install was started from, so without this the button would sit there
    // offering to install an app that is already installed.
    dismissInstall()
    notify()
  })
}

/** Re-render when Chrome's offer arrives or the app is installed. */
export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Whether Chrome has offered, and the offer has not been spent yet. */
export function canPromptInstall(): boolean {
  return deferred !== null
}

/**
 * Show Chrome's install dialog.
 *
 * The event is single-use — a second `prompt()` on the same one throws — so it
 * is discarded either way. A dismissal is not remembered here: the user may
 * have tapped the wrong thing, and Chrome will not re-offer on its own, so the
 * button staying is the only way back. The explicit × is what silences it.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred
  if (!event) return 'unavailable'

  deferred = null
  notify()

  await event.prompt()
  const { outcome } = await event.userChoice
  return outcome
}

/**
 * Whether the app is already running installed.
 *
 * `display-mode: standalone` covers Chrome and installed PWAs everywhere;
 * `navigator.standalone` is Safari's own, older answer and the only one iOS
 * gives. Both are checked because neither covers the other.
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false

  const standalone = (navigator as { standalone?: boolean }).standalone === true
  if (standalone) return true

  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.matchMedia?.('(display-mode: fullscreen)').matches === true ||
    window.matchMedia?.('(display-mode: minimal-ui)').matches === true
  )
}

/**
 * iOS, where installing is a menu item rather than an event.
 *
 * The touch-points clause is not defensive padding: iPadOS 13 and later report
 * a `Macintosh` user agent on purpose, so an iPad would otherwise be told it
 * cannot install — the one device where the manual instructions matter most,
 * since it is also the platform that will never fire `beforeinstallprompt`.
 */
export function isIos(userAgent: string, maxTouchPoints = 0): boolean {
  if (/iPad|iPhone|iPod/i.test(userAgent)) return true
  return /Macintosh/i.test(userAgent) && maxTouchPoints > 1
}

export function installDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    // Private mode or a full quota. The banner reappears next start, which is
    // mildly annoying and strictly better than a crash on the home screen.
    return false
  }
}

export function dismissInstall(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // Nothing to remember if storage is unavailable.
  }
}
